import { useCallback, useEffect, useRef, useState } from 'react'

import type { BillingUsageEntry } from '@/components/billing/billing.interface'
import type { SortOrder } from '@/components/data-table/data-table.interface'

import type { UsageHistoryParams } from './use-ledger-api.hook'
import { useUsageHistory } from './use-usage-history.hook'

export interface UsageFilterParams {
  search?: string
  stepType?: string[]
  dateFrom?: string
  dateTo?: string
  sortBy?: string
  sortOrder?: SortOrder
}

export interface UseUsagePaginationReturn {
  data: BillingUsageEntry[]
  isLoading: boolean
  error: string | null
  currentPage: number
  pageSize: number
  totalCount: number
  handlePageChange: (page: number) => void
  handlePageSizeChange: (pageSize: number) => void
  refetch: () => void
}

const SORT_FIELD_MAP: Record<string, string> = {
  costAmount: 'amount',
  type: 'stepType',
}

const mapSortField = (frontendField: string): string =>
  SORT_FIELD_MAP[frontendField] ?? frontendField

export const useUsagePagination = (
  initialPageSize = 25,
  filters: UsageFilterParams = {}
): UseUsagePaginationReturn => {
  const { data, total, isLoading, error, fetchUsageHistory } = useUsageHistory()
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const previousParamsRef = useRef<string>('')

  const buildApiParams = useCallback((): UsageHistoryParams => {
    const params: UsageHistoryParams = { page: currentPage, pageSize }
    if (filters.search) params.search = filters.search
    if (filters.stepType?.length) params.stepType = filters.stepType
    if (filters.dateFrom) params.dateFrom = filters.dateFrom
    if (filters.dateTo) params.dateTo = filters.dateTo
    if (filters.sortBy) params.sortBy = mapSortField(filters.sortBy) as UsageHistoryParams['sortBy']
    if (filters.sortOrder) params.sortOrder = filters.sortOrder as UsageHistoryParams['sortOrder']
    return params
  }, [currentPage, pageSize, filters])

  useEffect(() => {
    const params = buildApiParams()
    const paramsKey = JSON.stringify(params)
    if (paramsKey === previousParamsRef.current) return
    previousParamsRef.current = paramsKey
    void fetchUsageHistory(params)
  }, [buildApiParams, fetchUsageHistory])

  const handlePageChange = useCallback((page: number) => setCurrentPage(page), [])

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size)
    setCurrentPage(1)
  }, [])

  const refetch = useCallback(() => {
    const params = buildApiParams()
    void fetchUsageHistory(params)
  }, [buildApiParams, fetchUsageHistory])

  return {
    data,
    isLoading,
    error,
    currentPage,
    pageSize,
    totalCount: total,
    handlePageChange,
    handlePageSizeChange,
    refetch,
  }
}
